<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import {computed, PropType, useSlots} from "vue";

interface ListItem {
  title: string,
  url?: string,
  url_blank?: string,
  icon?: { name: string, viewBox: string },
}

const props = defineProps({
  title: {
    type: String,
    default: null
  },
  list: {
    type: Array as PropType<ListItem[]>,
    default: null
  },
  transparentTitle: {
	type: Boolean,
	defaul: false
  },
  transparentItem: {
	type: Boolean,
	defaul: false
  }
});

const slots = useSlots();

const modes = computed(() => {
	return {
		'part-navigation-list_transparent-title': props.transparentTitle,
		'part-navigation-list_transparent-item': props.transparentItem,
	};
});

const getComponentName = (item: ListItem): string => {
	if (item?.url) {
		return 'router-link';
	}
	if (item?.url_blank) {
		return 'a';
	}
	return 'div';
};

const getLinkAttributes = (item) => {
	const attrs = {};
	if (item?.url) {
		attrs.to = item.url;
	}
	if (item?.url_blank) {
		attrs.href = item.url_blank;
		attrs.target = '_blank';
	}
	return attrs;
};
</script>
<template>
	<div class="part-navigation-list" :class="modes">
		<span
			v-if="title"
			class="part-navigation-list__title font-18-pro-medium"
		>
			{{ title }}
		</span>
		<ul
			v-if="list"
			class="part-navigation-list__list"
		>
			<!-- Место для карточки в начале группы: карточка резерва в «Подписке»
				 стоит в одном столбце с обычными пунктами и с тем же зазором -->
			<li
				v-if="slots.prepend"
				class="part-navigation-list__list-item"
			>
				<slot name="prepend" />
			</li>
			<li
				v-for="(item, index) in list" :key="index"
				class="part-navigation-list__list-item"
			>
				<component
					:is="getComponentName(item)"
					v-bind="getLinkAttributes(item)"
					class="part-navigation-list__list-link"
				>
					<!-- Иконка в круге, как у карточки резерва: пункты меню и карточка
						 стоят в одном списке и должны выглядеть одинаково -->
					<span
						v-if="item.icon"
						class="part-navigation-list__list-icon-wrap"
					>
						<core-icon
							v-bind="item.icon"
							class="part-navigation-list__list-icon"
						/>
					</span>
					<span
						v-if="item.title"
						class="part-navigation-list__list-title"
					>
						{{ item.title }}
					</span>
					<core-icon
						v-if="item?.url || item?.url_blank"
						name="arrowLeft"
						viewBox="0 0 13 22"
						class="part-navigation-list__list-arrow"
					>
					</core-icon>
				</component>
			</li>
		</ul>
	</div>
</template>

<style lang="scss">
.part-navigation-list {
	&_transparent-title {
		.part-navigation-list {
			&__list-title {
				opacity: 0.5;
			}
		}
	}
	&_transparent-item {
		.part-navigation-list {
			&__list-link {
				background: transparent;
				border: none;
			}
		}
	}
  // Заголовок группы приглушён: он подписывает список, а не соперничает с
  // пунктами. Полупрозрачный цвет текста — тот же серый, что у подзаголовков
  // страниц, и он свой в каждой теме.
  &__title {
    margin-bottom: 1.3rem;
    display: block;
    color: rgba(var(--color-text-rgb), 0.5);
	@include min-desktop {
		@include font-24-pro-bold;
		margin-bottom: 1.6rem;
	}
  }

  &__list {
	display: flex;
	flex-direction: column;
	gap: 0.8rem;
  }

  &__list-item {
    display: block;
  }

  &__list-link {
    display: flex;
    align-items: center;
    gap: 1.6rem;
    fill: var(--color-active);
    color: var(--color-text);
    padding: 0.5rem 1.6rem;
	min-height: 5.2rem;
	background-color: var(--color-panel);
	// box-shadow: inset 0px 0px 1px 0px var(--color-purple);
	border: 1px solid var(--color-panel);
	border-radius: 1.6rem;
	transition: background-color 0.12s;

	// Подсветка только у ссылок и только там, где есть курсор: на тач-экране
	// hover залипает после тапа, а привязка к ширине окна прятала подсветку
	// в узком окне на ноутбуке.
	@media (hover: hover) and (pointer: fine) {
		&[href]:hover {
			background-color: rgba(var(--color-active-rgb), 0.10);
			.part-navigation-list__list-arrow {
				transform: rotate(-180deg) translateX(-0.5rem);
			}
		}
	}

	@include min-desktop {
		height: 6.2rem;
	}
  }

  &__list-icon-wrap {
	flex: 0 0 auto;
	width: 3.2rem;
	height: 3.2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background-color: var(--color-white);

	@include theme-dark {
		background-color: var(--color-panel);
		box-shadow: inset 1px 1px 1px 0 var(--color-panel);
	}
  }

  &__list-icon {
    width: 1.6rem;
    height: 1.6rem;
	@include min-desktop {
		width: 1.8rem;
		height: 1.8rem;
	}
  }

  &__list-arrow {
    width: 0.7rem;
    transform: rotate(-180deg);
    flex: 0 0 auto;
    margin: 0 0.6rem;
	transition: transform 0.12s;
	@include min-desktop {
		width: 0.8rem;
	}
  }

  &__list-title {
    flex: 1 1 auto;
    min-width: 0;
	@include min-desktop {
		@include font-18-pro-medium;
	}
  }
}
</style>
